import React, { Component } from 'react'
import Swal from 'sweetalert2'
import { AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle, AiOutlineInfoCircle } from "react-icons/ai"
import { getInformationWithDataPrefix, getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }
const BTN_DISABLED = { ...BTN_PRIMARY, background: '#94A3B8', cursor: 'not-allowed' }
const INPUT_STYLE = { border: '1px solid #E2E8F0', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.9rem', width: '220px' }
const LABEL_STYLE = { display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '0.25rem' }
const NOTICE_STYLE = { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.75rem 0.9rem', borderRadius: '8px', fontSize: '0.85rem', marginTop: '0.75rem' }
const NOTICE_INFO = { ...NOTICE_STYLE, background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E3A8A' }
const NOTICE_WARN = { ...NOTICE_STYLE, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }
const NOTICE_ERROR = { ...NOTICE_STYLE, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }
const NOTICE_OK = { ...NOTICE_STYLE, background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }

// /sales/update-customer only exists under /my and /alt, and each one is
// bound to its own Sage connection — so the prefix has to follow the Company
// dropdown, not the session's apiPrefix cookie.
const COMPANY_PREFIX = { 3: '/my', 6: '/alt' }
const COMPANY_LABEL = { 3: 'Mayaland (MS/PM)', 6: 'Altitude (AS/AL)' }

const SO_PATTERN = /^[A-Z0-9-]{1,20}$/
const SEARCH_MIN_CHARS = 2
const SEARCH_MAX_CHARS = 50
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MAX_SHOWN = 25
// The update endpoint only queues the message for Sage — "ok" doesn't mean
// it was applied, so the change is confirmed by reading the SO back.
const VERIFY_DELAYS_MS = [3000, 5000]

const STEPS = ['Load order', 'Select customer', 'Send to Sage', 'Verify', 'Update portal', 'Done']
const EDITABLE_PHASES = ['idle', 'loaded']

const errorMessageOf = (response) => {
    if (response && response.message) return response.message
    if (response && response.error) return response.error
    return ''
}

const isOk = (response) => !!(response && response.status && response.status.code === 1)

const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

const customerLabel = (c) => c ? `${c.customerName} (${c.customerNo})` : ''

const ORDER_STATE = {
    order: null,          // { salesOrderNo, orderDate, customerNo, customerName, salespersonNo }
    currentLevel: null,   // customer_price_level of the SO's current customer (default 1)
    portalRecord: null,   // last customer the portal recorded for this SO, or null
    loadError: '',
    search: '',
    searching: false,
    searchError: '',
    searchOpen: false,
    results: [],          // [{ customerNo, customerName, level, isCurrent, disabled }]
    activeIndex: -1,
    selected: null,
    verifyAttempt: 0,
    flowError: '',
    summary: null,        // { mode, from, to, finishedAt }
}

/**
 * Tools → Customer Correction.
 *
 * 2026-09-25: born as a portal-only fix — an SO's customer got reassigned
 * directly in Sage (e.g. a "Cash Sale" placeholder → the real customer) and
 * Invoices V3's mismatch check kept blocking it against the stale
 * creation-time snapshot. /v3/audit/correct-customer pulls the customer Sage
 * ALREADY has and records it as the new reference point (APPEND to
 * document_audit_trail + sync of sales_orders_aws_v2).
 *
 * 2026-10-06: the tool now does the Sage side too, end to end:
 *   1. Load the SO live from Sage (blocked if not found or already invoiced).
 *   2. Pick the new customer — only customers on the SAME portal price level
 *      (customer_price_level) can be picked, so the order's prices stay valid.
 *      Other levels are listed but disabled; those still go through the
 *      manual Sage edit + "Update portal only".
 *   3. Confirm → /sales/update-customer (queues the change for Sage).
 *   4. Verify by reading the SO back (3s, then one retry at 5s). If it still
 *      doesn't match, never resend automatically — the queued change may
 *      still land later.
 *   5. Runs the original portal correction automatically.
 */
export class CustomerCorrectionTool extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isAdmin: getValueCookie('TypeUser') === '1',
            docNo: '',
            idCompany: getValueCookie('CompanyId') === '6' ? 6 : 3,
            phase: 'idle',   // idle | loading | loaded | sending | verifying | verifyFailed | applying | applyFailed | done
            ...ORDER_STATE,
        }
        this._timers = []
        this._searchTimer = null
        this._searchSeq = 0
        this._unloadGuard = false
        this._unmounted = false
    }

    componentWillUnmount() {
        this._unmounted = true
        this._timers.forEach(clearTimeout)
        clearTimeout(this._searchTimer)
        this.setUnloadGuard(false)
    }

    wait = (ms) => new Promise((resolve) => { this._timers.push(setTimeout(resolve, ms)) })

    handleBeforeUnload = (e) => {
        e.preventDefault()
        e.returnValue = ''
    }

    setUnloadGuard(on) {
        if (on && !this._unloadGuard) window.addEventListener('beforeunload', this.handleBeforeUnload)
        if (!on && this._unloadGuard) window.removeEventListener('beforeunload', this.handleBeforeUnload)
        this._unloadGuard = on
    }

    resetOrder(extra = {}) {
        clearTimeout(this._searchTimer)
        this._searchSeq++
        this.setState({ phase: 'idle', ...ORDER_STATE, ...extra })
    }

    // ── Data access ──────────────────────────────────────────────────────

    async fetchSageHeader(docNo, idCompany) {
        const resp = await getInformationWithDataPrefix('/sales/get/header/by-no', { SalesOrderNo: docNo, idCompany }, COMPANY_PREFIX[idCompany])
        if (!isOk(resp)) return { error: true, row: null }
        const rows = Array.isArray(resp.data) ? resp.data : []
        // Match on SalesOrderNo too, not just data[0] — the WS bridge has
        // returned responses for the wrong request before.
        return { error: false, row: rows.find((r) => String(r.SalesOrderNo).toUpperCase() === docNo) || null }
    }

    async fetchInvoiceLink(docNo, idCompany) {
        const resp = await getInformationWithDataV3('/v3/audit/trail', { docNo, idCompany })
        if (!isOk(resp)) return { error: true }
        const events = Array.isArray(resp.data) ? resp.data : []
        const invoiced = events.find((e) => e.action === 'converted_to_invoice' && String(e.linkedSoNo).toUpperCase() === docNo)
        return { error: false, invoiceNo: invoiced ? invoiced.docNo : null }
    }

    async fetchPortalRecord(docNo, idCompany) {
        const resp = await getInformationWithDataV3('/v3/audit/original-customer', { docNo, idCompany })
        const data = isOk(resp) ? resp.data : null
        return data && data.found ? { customerNo: String(data.customerNo), customerName: data.customerName || '' } : null
    }

    async searchCustomers(search, idCompany) {
        const resp = await getInformationWithDataV3('/v3/pricing/customer-level/list', { idCompany, search, page: 1, pageSize: 50 })
        if (!isOk(resp)) return null
        return resp.data && Array.isArray(resp.data.customers) ? resp.data.customers : []
    }

    // Same default as the pricing backend: no customer_price_level row → level 1.
    async fetchPortalLevel(customerNo, idCompany) {
        const customers = await this.searchCustomers(customerNo, idCompany)
        if (customers === null) return null
        const match = customers.find((c) => String(c.customerNo) === customerNo)
        return match ? Number(match.priceLevel) || 1 : 1
    }

    // ── Step 1: load ─────────────────────────────────────────────────────

    handleLoad = async () => {
        const docNo = this.state.docNo.trim().toUpperCase()
        if (!SO_PATTERN.test(docNo)) {
            this.resetOrder({ docNo, loadError: 'Enter a valid Sales Order No. (letters and numbers only, e.g. MS70157).' })
            return
        }
        const idCompany = this.state.idCompany
        this.resetOrder({ docNo, phase: 'loading' })

        const [header, invoice, portalRecord] = await Promise.all([
            this.fetchSageHeader(docNo, idCompany),
            this.fetchInvoiceLink(docNo, idCompany),
            this.fetchPortalRecord(docNo, idCompany),
        ])
        if (this._unmounted) return

        if (header.error) {
            this.setState({ phase: 'idle', loadError: 'Could not reach Sage. Please try again.' })
            return
        }
        if (!header.row || !header.row.CustomerNo) {
            this.setState({ phase: 'idle', loadError: `${docNo} was not found in Sage for ${COMPANY_LABEL[idCompany]} — it doesn't exist, or it was already invoiced or closed.` })
            return
        }
        if (invoice.error) {
            this.setState({ phase: 'idle', loadError: `Could not confirm whether ${docNo} was already invoiced. Please try again.` })
            return
        }
        if (invoice.invoiceNo) {
            this.setState({ phase: 'idle', loadError: `${docNo} was already invoiced (${invoice.invoiceNo}). Its customer can't be changed.` })
            return
        }

        const order = {
            salesOrderNo: docNo,
            orderDate: header.row.OrderDate || '',
            customerNo: String(header.row.CustomerNo),
            customerName: header.row.BillToName || '',
            salespersonNo: header.row.SalespersonNo || '',
        }
        const currentLevel = await this.fetchPortalLevel(order.customerNo, idCompany)
        if (this._unmounted) return
        if (currentLevel === null) {
            this.setState({ phase: 'idle', loadError: "Could not load the current customer's price level. Please try again." })
            return
        }

        this.setState({ phase: 'loaded', order, currentLevel, portalRecord })
    }

    // ── Step 2: search / select ──────────────────────────────────────────

    handleSearchChange = (e) => {
        const search = e.target.value.slice(0, SEARCH_MAX_CHARS)
        clearTimeout(this._searchTimer)
        this.setState({ search, selected: null, searchError: '' })

        const term = search.trim()
        if (term.length < SEARCH_MIN_CHARS) {
            this._searchSeq++
            this.setState({ results: [], searching: false, searchOpen: false, activeIndex: -1 })
            return
        }
        this._searchTimer = setTimeout(() => this.runSearch(term), SEARCH_DEBOUNCE_MS)
    }

    runSearch = async (term) => {
        const seq = ++this._searchSeq
        this.setState({ searching: true })
        const customers = await this.searchCustomers(term, this.state.idCompany)
        // Drop responses for an older search term.
        if (this._unmounted || seq !== this._searchSeq) return

        if (customers === null) {
            this.setState({ searching: false, results: [], searchOpen: false, searchError: 'Could not search customers. Please try again.' })
            return
        }

        const { order, currentLevel } = this.state
        const results = customers
            .map((c) => {
                const customerNo = String(c.customerNo)
                const level = Number(c.priceLevel) || 1
                const isCurrent = customerNo === order.customerNo
                return { customerNo, customerName: c.customerName || '', level, isCurrent, disabled: isCurrent || level !== currentLevel }
            })
            .sort((a, b) => (a.disabled - b.disabled) || a.customerName.localeCompare(b.customerName))

        const visible = results.slice(0, SEARCH_MAX_SHOWN)
        this.setState({ searching: false, results, searchOpen: true, activeIndex: visible.findIndex((r) => !r.disabled) })
    }

    handleSearchKeyDown = (e) => {
        const { results, activeIndex, searchOpen } = this.state
        const visible = results.slice(0, SEARCH_MAX_SHOWN)

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            if (!searchOpen) {
                if (visible.length) this.setState({ searchOpen: true })
                return
            }
            const step = e.key === 'ArrowDown' ? 1 : -1
            let i = activeIndex < 0 ? (step > 0 ? -1 : 0) : activeIndex
            for (let n = 0; n < visible.length; n++) {
                i = (i + step + visible.length) % visible.length
                if (!visible[i].disabled) {
                    this.setState({ activeIndex: i })
                    break
                }
            }
        } else if (e.key === 'Enter') {
            const option = visible[activeIndex]
            if (searchOpen && option && !option.disabled) {
                e.preventDefault()
                this.selectCustomer(option)
            }
        } else if (e.key === 'Escape') {
            this.setState({ searchOpen: false })
        }
    }

    selectCustomer(customer) {
        if (customer.disabled) return
        this.setState({ selected: customer, search: `${customer.customerNo} — ${customer.customerName}`, searchOpen: false })
    }

    // ── Steps 3–5: send, verify, update portal ───────────────────────────

    handleSend = async () => {
        const { order, selected, idCompany, currentLevel } = this.state
        if (!order || !selected || selected.disabled) return

        const confirmation = await Swal.fire({
            title: 'Change the customer in Sage?',
            html: `
                <div style="text-align:left;font-size:0.9rem;line-height:1.6">
                    <div><strong>Sales Order:</strong> ${escapeHtml(order.salesOrderNo)} · ${escapeHtml(COMPANY_LABEL[idCompany])}</div>
                    <div><strong>From:</strong> ${escapeHtml(customerLabel(order))}</div>
                    <div><strong>To:</strong> ${escapeHtml(customerLabel(selected))}</div>
                    <div><strong>Price level:</strong> ${escapeHtml(currentLevel)} (unchanged)</div>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            focusCancel: true,
            confirmButtonText: 'Yes, change it',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#2563EB',
            cancelButtonColor: '#64748B',
        })
        if (!confirmation.isConfirmed || this._unmounted) return

        this.setUnloadGuard(true)
        this.setState({ phase: 'sending', flowError: '' })

        // TEMPORAL (2026-10-06): se usa /Test/EnvioOrden (ruta base, sin
        // prefijo) en lugar de {prefix}/sales/update-customer porque por el
        // límite de los WebSockets no se puede cambiar backv2 ahora mismo.
        // Ese handler siempre envía por apiMAS90 — el worker decide la
        // empresa por `IdCompany` y la acción por `OrderType`. Cuando
        // backv2 se pueda desplegar, regresar a:
        //   getInformationWithDataPrefix('/sales/update-customer', { SalesOrderNo, CustomerNo, IdCompany }, COMPANY_PREFIX[idCompany])
        const resp = await getInformationWithDataPrefix('/Test/EnvioOrden', {
            SalesOrderNo: order.salesOrderNo,
            CustomerNo: selected.customerNo,
            IdCompany: idCompany,
            OrderType: 'UpdateSalesOrderCustomer',
        }, '')
        if (this._unmounted) return

        // A 4xx is a definite rejection — nothing reached Sage. A timeout,
        // network error or 5xx is ambiguous (it may have been queued), so
        // those still go through verification instead of inviting a resend.
        if (!isOk(resp) && resp && resp.httpStatus >= 400 && resp.httpStatus < 500) {
            this.setUnloadGuard(false)
            this.setState({ phase: 'loaded', flowError: `Sage rejected the request${errorMessageOf(resp) ? `: ${errorMessageOf(resp)}` : ''}. Nothing was changed — you can try again.` })
            return
        }

        this.setState({ phase: 'verifying', verifyAttempt: 1 })
        let lastSeen = ''
        for (let i = 0; i < VERIFY_DELAYS_MS.length; i++) {
            this.setState({ verifyAttempt: i + 1 })
            await this.wait(VERIFY_DELAYS_MS[i])
            if (this._unmounted) return

            const { row, error } = await this.fetchSageHeader(order.salesOrderNo, idCompany)
            if (this._unmounted) return

            if (row && String(row.CustomerNo) === selected.customerNo) {
                await this.applyPortal({ mode: 'sage', from: order, to: selected })
                return
            }
            lastSeen = error ? 'no response from Sage' : row ? `still ${row.BillToName} (${row.CustomerNo})` : 'order not found'
        }

        this.setUnloadGuard(false)
        this.setState({
            phase: 'verifyFailed',
            flowError: `Sage doesn't show the new customer yet (${lastSeen}). The change may still be applied — reload the order and check the customer BEFORE sending it again.`,
        })
    }

    // The original portal correction (APPEND to document_audit_trail + sync
    // of sales_orders_aws_v2). It re-reads the customer from Sage itself.
    applyPortal = async (summary) => {
        const { order, idCompany } = this.state
        this.setUnloadGuard(true)
        this.setState({ phase: 'applying', flowError: '', summary })

        const reason = summary.mode === 'sage'
            ? 'Customer changed in Sage from Tools: Customer Correction'
            : 'Corregido desde Tools: Customer Correction'
        const resp = await getInformationWithDataV3('/v3/audit/correct-customer', { docNo: order.salesOrderNo, idCompany, reason })
        if (this._unmounted) return
        this.setUnloadGuard(false)

        if (isOk(resp) && resp.data) {
            // changed:false means the portal already had this customer
            // recorded — still a successful end state.
            const to = { customerNo: String(resp.data.customerNo), customerName: resp.data.customerName || summary.to.customerName }
            this.setState({
                phase: 'done',
                order: { ...order, ...to },
                summary: { ...summary, to, finishedAt: new Date().toLocaleString() },
            })
        } else {
            this.setState({ phase: 'applyFailed', flowError: errorMessageOf(resp) || 'The portal could not be updated.' })
        }
    }

    handleSyncPortalOnly = () => {
        const { order, portalRecord } = this.state
        this.applyPortal({ mode: 'portal', from: portalRecord, to: order })
    }

    handleRetryPortal = () => this.applyPortal(this.state.summary)

    handleStartOver = () => this.resetOrder({ docNo: '' })

    // ── Render ───────────────────────────────────────────────────────────

    stepStatus() {
        const { phase, selected } = this.state
        switch (phase) {
            case 'loading': return { index: 0, state: 'progress' }
            case 'loaded': return selected ? { index: 2, state: 'current' } : { index: 1, state: 'current' }
            case 'sending': return { index: 2, state: 'progress' }
            case 'verifying': return { index: 3, state: 'progress' }
            case 'verifyFailed': return { index: 3, state: 'error' }
            case 'applying': return { index: 4, state: 'progress' }
            case 'applyFailed': return { index: 4, state: 'error' }
            case 'done': return { index: STEPS.length, state: 'done' }
            default: return { index: 0, state: 'current' }
        }
    }

    renderStepper() {
        const { index, state } = this.stepStatus()
        const { verifyAttempt } = this.state
        const COLORS = {
            done: { bg: '#16A34A', fg: '#FFFFFF', text: '#166534' },
            current: { bg: '#2563EB', fg: '#FFFFFF', text: '#1E3A8A' },
            progress: { bg: '#2563EB', fg: '#FFFFFF', text: '#1E3A8A' },
            error: { bg: '#DC2626', fg: '#FFFFFF', text: '#991B1B' },
            pending: { bg: '#E2E8F0', fg: '#475569', text: '#64748B' },
        }

        return (
            <ol aria-label="Progress" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
                {STEPS.map((label, i) => {
                    const s = i < index || state === 'done' ? 'done' : i === index ? state : 'pending'
                    const c = COLORS[s]
                    const text = i === 3 && s !== 'pending' && s !== 'done' ? `${label} (${verifyAttempt || 1}/${VERIFY_DELAYS_MS.length})` : label
                    return (
                        <li key={label} aria-current={i === index ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', fontWeight: i === index ? 700 : 500, color: c.text }}>
                            <span style={{ width: '1.4rem', height: '1.4rem', borderRadius: '50%', background: c.bg, color: c.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700 }}>
                                {s === 'progress'
                                    ? <span className="spinner-border spinner-border-sm" style={{ width: '0.8rem', height: '0.8rem', borderWidth: '0.12em' }} role="status" aria-hidden="true"></span>
                                    : s === 'done' ? '✓' : s === 'error' ? '!' : i + 1}
                            </span>
                            {text}
                        </li>
                    )
                })}
            </ol>
        )
    }

    renderOrderCard() {
        const { order, currentLevel, portalRecord, phase } = this.state
        const portalDiffers = portalRecord && portalRecord.customerNo !== order.customerNo

        return (
            <div style={{ marginTop: '1rem', padding: '0.9rem 1rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.6rem 1rem', fontSize: '0.85rem' }}>
                    <div><div style={LABEL_STYLE}>Sales Order</div><strong>{order.salesOrderNo}</strong></div>
                    <div><div style={LABEL_STYLE}>Order date</div>{order.orderDate || '—'}</div>
                    <div><div style={LABEL_STYLE}>Salesperson</div>{order.salespersonNo || '—'}</div>
                    <div><div style={LABEL_STYLE}>Price level</div><strong>Level {currentLevel}</strong></div>
                    <div style={{ gridColumn: '1 / -1' }}><div style={LABEL_STYLE}>Current customer (live in Sage)</div><strong>{customerLabel(order)}</strong></div>
                </div>

                {portalDiffers && phase === 'loaded' && (
                    <div style={NOTICE_WARN}>
                        <AiOutlineWarning style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                        <div>
                            The portal still has <strong>{customerLabel(portalRecord)}</strong> recorded for this order. If the customer was already changed manually in Sage, update the portal only.
                            <div style={{ marginTop: '0.5rem' }}>
                                <button style={BTN_SECONDARY} onClick={this.handleSyncPortalOnly}>Update portal only</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    renderCustomerSearch() {
        const { search, searching, searchError, searchOpen, results, activeIndex, selected, currentLevel, phase } = this.state
        const visible = results.slice(0, SEARCH_MAX_SHOWN)
        const editable = phase === 'loaded'
        const term = search.trim()

        return (
            <div style={{ marginTop: '1.25rem' }}>
                <label htmlFor="cc-customer-search" style={LABEL_STYLE}>New customer (search by number or name)</label>
                <div style={{ position: 'relative', maxWidth: '520px' }}>
                    <input
                        id="cc-customer-search"
                        role="combobox"
                        aria-expanded={searchOpen}
                        aria-controls="cc-customer-options"
                        aria-autocomplete="list"
                        aria-activedescendant={searchOpen && activeIndex >= 0 ? `cc-opt-${activeIndex}` : undefined}
                        autoComplete="off"
                        style={{ ...INPUT_STYLE, width: '100%' }}
                        value={search}
                        disabled={!editable}
                        maxLength={SEARCH_MAX_CHARS}
                        placeholder="e.g. 0001234 or store name"
                        onChange={this.handleSearchChange}
                        onKeyDown={this.handleSearchKeyDown}
                        onFocus={() => visible.length && !selected && this.setState({ searchOpen: true })}
                        onBlur={() => this.setState({ searchOpen: false })}
                    />
                    {searching && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ position: 'absolute', right: '0.75rem', top: '0.7rem', color: '#64748B' }}></span>
                    )}

                    {searchOpen && (
                        <ul id="cc-customer-options" role="listbox"
                            onMouseDown={(e) => e.preventDefault()}
                            style={{ position: 'absolute', zIndex: 20, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: '300px', overflowY: 'auto', margin: 0, padding: '0.25rem 0', listStyle: 'none', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}>
                            {visible.length === 0 && (
                                <li style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#64748B' }}>No customers match "{term}".</li>
                            )}
                            {visible.map((c, i) => (
                                <li key={c.customerNo} id={`cc-opt-${i}`} role="option" aria-selected={i === activeIndex} aria-disabled={c.disabled}
                                    onClick={() => this.selectCustomer(c)}
                                    onMouseEnter={() => !c.disabled && this.setState({ activeIndex: i })}
                                    style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem',
                                        padding: '0.45rem 0.75rem', fontSize: '0.85rem',
                                        cursor: c.disabled ? 'not-allowed' : 'pointer',
                                        color: c.disabled ? '#94A3B8' : '#0F172A',
                                        background: i === activeIndex && !c.disabled ? '#EFF6FF' : 'transparent',
                                    }}>
                                    <span><strong>{c.customerNo}</strong> — {c.customerName}</span>
                                    <span style={{ flexShrink: 0, fontSize: '0.72rem', fontWeight: 600, padding: '0.1rem 0.45rem', borderRadius: '999px', background: c.disabled ? '#F1F5F9' : '#DCFCE7', color: c.disabled ? '#64748B' : '#166534' }}>
                                        {c.isCurrent ? 'Current' : `Level ${c.level}`}
                                    </span>
                                </li>
                            ))}
                            {results.length > SEARCH_MAX_SHOWN && (
                                <li style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', color: '#64748B', borderTop: '1px solid #F1F5F9' }}>
                                    Showing the first {SEARCH_MAX_SHOWN} of {results.length} — keep typing to narrow it down.
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {term.length > 0 && term.length < SEARCH_MIN_CHARS && !selected && (
                    <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#64748B' }}>Type at least {SEARCH_MIN_CHARS} characters.</div>
                )}
                {searchError && <div style={{ marginTop: '0.5rem', color: '#DC2626', fontSize: '0.85rem' }}>{searchError}</div>}

                <div style={NOTICE_INFO}>
                    <AiOutlineInfoCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <span>
                        Only <strong>Level {currentLevel}</strong> customers can be selected, so the order's prices stay the same. If the customer you need is disabled or not listed, it's on another price level or was added to Sage today — change it manually in Sage for now, then use <em>Update portal only</em>.
                    </span>
                </div>
            </div>
        )
    }

    renderFlowStatus() {
        const { phase, flowError, verifyAttempt, summary, selected } = this.state

        if (phase === 'sending') {
            return <div style={NOTICE_INFO}><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Sending the change to Sage...</div>
        }
        if (phase === 'verifying') {
            return verifyAttempt > 1
                ? <div style={NOTICE_WARN} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Sage hasn't confirmed it yet — retrying ({verifyAttempt}/{VERIFY_DELAYS_MS.length})...</div>
                : <div style={NOTICE_INFO} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Waiting for Sage to confirm {customerLabel(selected)}...</div>
        }
        if (phase === 'verifyFailed') {
            return (
                <div style={NOTICE_ERROR} role="alert">
                    <AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <div>
                        {flowError}
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button style={BTN_PRIMARY} onClick={this.handleLoad}>Reload order</button>
                            <button style={BTN_SECONDARY} onClick={this.handleStartOver}>Start over</button>
                        </div>
                    </div>
                </div>
            )
        }
        if (phase === 'applying') {
            return <div style={NOTICE_INFO} role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> {summary && summary.mode === 'sage' ? 'Sage confirmed the change. ' : ''}Updating the portal records...</div>
        }
        if (phase === 'applyFailed') {
            return (
                <div style={NOTICE_ERROR} role="alert">
                    <AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <div>
                        {summary && summary.mode === 'sage' && <><strong>Sage was updated, but the portal is still pending.</strong> </>}
                        {flowError} Don't send the change to Sage again — just retry the portal update.
                        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button style={BTN_PRIMARY} onClick={this.handleRetryPortal}>Retry portal update</button>
                            <button style={BTN_SECONDARY} onClick={this.handleStartOver}>Start over</button>
                        </div>
                    </div>
                </div>
            )
        }
        if (phase === 'done' && summary) {
            return (
                <div style={NOTICE_OK} role="status">
                    <AiOutlineCheckCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                    <div>
                        <strong>{summary.mode === 'sage' ? 'Customer changed in Sage and the portal.' : 'Portal updated to match Sage.'}</strong>
                        <div style={{ marginTop: '0.35rem', lineHeight: 1.6 }}>
                            <div>Sales Order: <strong>{this.state.order.salesOrderNo}</strong></div>
                            {summary.from && <div>From: {customerLabel(summary.from)}</div>}
                            <div>To: <strong>{customerLabel(summary.to)}</strong></div>
                            <div style={{ fontSize: '0.75rem', color: '#15803D' }}>{summary.finishedAt} · The original creation record was kept.</div>
                        </div>
                        <div style={{ marginTop: '0.6rem' }}>
                            <button style={BTN_PRIMARY} onClick={this.handleStartOver}>New correction</button>
                        </div>
                    </div>
                </div>
            )
        }
        if (flowError) {
            return <div style={NOTICE_ERROR} role="alert"><AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} /> {flowError}</div>
        }
        return null
    }

    render() {
        const { isAdmin, docNo, idCompany, phase, loadError, order, selected } = this.state

        if (!isAdmin) {
            return (
                <div className="dash-page" style={{ paddingTop: '70px' }}>
                    <Menu />
                    <div style={{ padding: '2rem' }}>This tool is only available to administrators.</div>
                </div>
            )
        }

        const editable = EDITABLE_PHASES.includes(phase)
        const canLoad = editable && docNo.trim().length > 0
        const canSend = phase === 'loaded' && selected && !selected.disabled

        return (
            <div className="dash-page" style={{ paddingTop: '70px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem' }}>
                    <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Customer Correction</h1>
                    <p style={{ color: 'rgba(255,255,255,0.75)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                        Change a Sales Order's customer in Sage (same price level only) and update the portal's records automatically, without erasing the original audit record.
                    </p>
                </div>

                <div style={{ padding: '1.5rem 1rem', maxWidth: '760px' }}>
                    <div className="card mb-3">
                        <div className="card-body">
                            {this.renderStepper()}

                            <form style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
                                onSubmit={(e) => { e.preventDefault(); if (canLoad) this.handleLoad() }}>
                                <div>
                                    <label htmlFor="cc-docno" style={LABEL_STYLE}>Sales Order No.</label>
                                    <input id="cc-docno" style={INPUT_STYLE} value={docNo} placeholder="e.g. MS70157" maxLength={20} disabled={!editable}
                                        onChange={(e) => this.resetOrder({ docNo: e.target.value.toUpperCase() })} />
                                </div>
                                <div>
                                    <label htmlFor="cc-company" style={LABEL_STYLE}>Company</label>
                                    <select id="cc-company" style={INPUT_STYLE} value={idCompany} disabled={!editable}
                                        onChange={(e) => this.resetOrder({ docNo, idCompany: Number(e.target.value) })}>
                                        <option value={3}>{COMPANY_LABEL[3]}</option>
                                        <option value={6}>{COMPANY_LABEL[6]}</option>
                                    </select>
                                </div>
                                <button type="submit" style={canLoad ? BTN_PRIMARY : BTN_DISABLED} disabled={!canLoad}>
                                    {phase === 'loading' ? 'Loading...' : 'Load order'}
                                </button>
                            </form>

                            {phase === 'loading' && (
                                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.85rem' }}>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Looking up the live Sage record...
                                </div>
                            )}

                            {loadError && (
                                <div style={NOTICE_ERROR} role="alert"><AiOutlineCloseCircle style={{ flexShrink: 0, marginTop: '0.15rem' }} /> {loadError}</div>
                            )}

                            {order && this.renderOrderCard()}
                            {order && phase !== 'done' && this.renderCustomerSearch()}

                            {order && (phase === 'loaded' || phase === 'sending') && (
                                <div style={{ marginTop: '1rem' }}>
                                    <button style={canSend ? BTN_PRIMARY : BTN_DISABLED} disabled={!canSend} onClick={this.handleSend}>
                                        {phase === 'sending' ? 'Sending...' : 'Change customer'}
                                    </button>
                                </div>
                            )}

                            {this.renderFlowStatus()}
                        </div>
                    </div>

                    <p style={{ fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic' }}>
                        The change is sent to Sage and confirmed by reading the order back before the portal is updated. If Sage doesn't confirm it, never resend right away — reload the order first, since the change may still be applied.
                    </p>
                </div>
            </div>
        )
    }
}

export default CustomerCorrectionTool
